import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import LearningSideBar from '../components/learning/LearningSideBar';
import PostContent from '../components/learning/PostContent';
import CommentSection from '../components/learning/CommentSection';
import LearningHero from '../components/learning/LearningHero';

import { categoryService } from '../../services/categoryService';
import { postService } from '../../services/postService';
import Footer from '../components/common/Footer'
import Navbar from '../components/common/Navbar'
import '../css/Learning.css';

const Learning = () => {
  const { postId } = useParams();
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('all');

  const [allPosts, setAllPosts] = useState([]);
  const [regularPosts, setRegularPosts] = useState([]);
  const [searchResults, setSearchResults] = useState([]);

  const [selectedPost, setSelectedPost] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const [isContentLoading, setIsContentLoading] = useState(true);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchMode, setIsSearchMode] = useState(false);

  const createSlug = (title = '') =>
    title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

  const navigateToPost = useCallback(
    (post, replace = false) => {
      if (!post) return;

      navigate(
        `/learning-zone/${post.id}/${createSlug(post.title)}`,
        { replace }
      );
    },
    [navigate]
  );

  const currentPosts = isSearchMode ? searchResults : regularPosts;

  // Load categories - no loading state for this as navbar/hero are static
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await categoryService.getAllTutorial();
        setCategories(response.data || []);
      } catch (error) {
        console.error('Failed to load categories:', error);
        setCategories([]);
      }
    };

    loadCategories();
  }, []);

  // Load all posts - this is the main content loading
  useEffect(() => {
    const loadAllPosts = async () => {
      setIsContentLoading(true);
      try {
        const response = await postService.getArticleSummary();
        const posts = response.data || [];

        setAllPosts(posts);
        setRegularPosts(posts);
        setSelectedPost(posts[0] || null);
        setCurrentIndex(0);
      } catch (error) {
        console.error('Failed to load all posts:', error);
        setAllPosts([]);
        setRegularPosts([]);
      } finally {
        setIsContentLoading(false);
      }
    };

    loadAllPosts();
  }, []);

  // Exit search mode
  const exitSearchMode = useCallback(() => {
    setIsSearchMode(false);
    setSearchQuery('');
    setSearchResults([]);

    if (selectedCategoryId === 'all') {
      setRegularPosts(allPosts);

      if (allPosts.length > 0) {
        const exists = allPosts.some(
          (post) => post.id === selectedPost?.id
        );

        const targetPost = exists
          ? selectedPost
          : allPosts[0];

        setSelectedPost(targetPost);
        setCurrentIndex(
          allPosts.findIndex((p) => p.id === targetPost?.id) || 0
        );

        navigateToPost(targetPost, true);
      }
    } else {
      const loadCategoryPosts = async () => {
        try {
          const response =
            await postService.getTutorialsByCategory(
              selectedCategoryId
            );

          const posts = response.data || [];

          setRegularPosts(posts);

          if (posts.length > 0) {
            setSelectedPost(posts[0]);
            setCurrentIndex(0);
            navigateToPost(posts[0], true);
          } else {
            setSelectedPost(null);
          }
        } catch (error) {
          console.error('Failed to load posts:', error);
          setRegularPosts([]);
          setSelectedPost(null);
        }
      };

      loadCategoryPosts();
    }
  }, [
    selectedCategoryId,
    allPosts,
    selectedPost,
    navigateToPost,
  ]);

  // Search tutorials
  const handleSearch = useCallback(
    async (query) => {
      if (!query.trim()) {
        exitSearchMode();
        return;
      }

      setIsSearchLoading(true);
      setIsSearchMode(true);
      setSearchQuery(query);

      try {
        const response = await postService.searchTutorials(query);
        const results = response.data || [];

        setSearchResults(results);

        if (results.length > 0) {
          setSelectedPost(results[0]);
          setCurrentIndex(0);
          navigateToPost(results[0], true);
        } else {
          setSelectedPost(null);
          setCurrentIndex(0);
        }
      } catch (error) {
        console.error('Failed to search tutorials:', error);
        setSearchResults([]);
        setSelectedPost(null);
      } finally {
        setIsSearchLoading(false);
      }
    },
    [navigateToPost, exitSearchMode]
  );

  // Select post
  const handlePostSelect = useCallback(
    (post, index) => {
      if (!post) return;

      const postIndex =
        index !== undefined
          ? index
          : currentPosts.findIndex((p) => p.id === post.id);

      setCurrentIndex(postIndex);
      setSelectedPost(post);

      navigateToPost(post);
      setIsMobileMenuOpen(false);
    },
    [currentPosts, navigateToPost]
  );

  // Select category
  const handleCategorySelect = useCallback(
    async (categoryId) => {
      if (isSearchMode) {
        setIsSearchMode(false);
        setSearchQuery('');
        setSearchResults([]);
      }

      setSelectedCategoryId(categoryId);
      setSelectedPost(null);
      setCurrentIndex(0);
      setIsContentLoading(true);

      if (categoryId === 'all') {
        setRegularPosts(allPosts);

        if (allPosts.length > 0) {
          handlePostSelect(allPosts[0], 0);
        }

        setIsContentLoading(false);
        return;
      }

      try {
        const response =
          await postService.getTutorialsByCategory(categoryId);

        const posts = response.data || [];

        setRegularPosts(posts);

        if (posts.length > 0) {
          handlePostSelect(posts[0], 0);
        }
      } catch (error) {
        console.error('Failed to load posts:', error);
        setRegularPosts([]);
      } finally {
        setIsContentLoading(false);
      }
    },
    [allPosts, handlePostSelect, isSearchMode]
  );

  // Next post
  const handleNextPost = useCallback(() => {
    if (currentIndex >= currentPosts.length - 1) return;

    const nextIndex = currentIndex + 1;
    const nextPost = currentPosts[nextIndex];

    setCurrentIndex(nextIndex);
    setSelectedPost(nextPost);

    navigateToPost(nextPost);

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, [currentIndex, currentPosts, navigateToPost]);

  // Previous post
  const handlePreviousPost = useCallback(() => {
    if (currentIndex <= 0) return;

    const prevIndex = currentIndex - 1;
    const prevPost = currentPosts[prevIndex];

    setCurrentIndex(prevIndex);
    setSelectedPost(prevPost);

    navigateToPost(prevPost);

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, [currentIndex, currentPosts, navigateToPost]);

  // Load category posts
  useEffect(() => {
    if (isSearchMode) return;

    const loadPosts = async () => {
      if (selectedCategoryId === 'all') {
        setRegularPosts(allPosts);
        setCurrentIndex(0);
        setSelectedPost(allPosts[0] || null);
        return;
      }

      try {
        const response =
          await postService.getTutorialsByCategory(
            selectedCategoryId
          );

        const posts = response.data || [];

        setRegularPosts(posts);
        setCurrentIndex(0);
        setSelectedPost(posts[0] || null);
      } catch (error) {
        console.error('Failed to load posts:', error);
        setRegularPosts([]);
      }
    };

    loadPosts();
  }, [selectedCategoryId, allPosts, isSearchMode]);

  // Handle direct URL access
  useEffect(() => {
    if (!postId || isContentLoading) return;

    const findPost = async () => {
      let foundPost = currentPosts.find(
        (post) => post.id === parseInt(postId)
      );

      if (!foundPost && isSearchMode) {
        try {
          const response = await postService.getById(
            parseInt(postId)
          );

          foundPost = response.data;

          if (
            foundPost &&
            !searchResults.some((p) => p.id === foundPost.id)
          ) {
            setSearchResults((prev) => [foundPost, ...prev]);
          }
        } catch (error) {
          console.error('Failed to fetch post:', error);
        }
      }

      if (foundPost) {
        const index = currentPosts.findIndex(
          (post) => post.id === foundPost.id
        );

        setSelectedPost(foundPost);
        setCurrentIndex(index !== -1 ? index : 0);
      }
    };

    findPost();
  }, [
    postId,
    isContentLoading,
    isSearchMode,
    currentPosts,
    searchResults,
  ]);

  // Content Loading Skeleton
  const ContentLoadingSkeleton = () => (
    <div className="learn-content-loading">
      <div className="learn-sidebar-skeleton">
        <div className="learn-skeleton-header"></div>
        {[...Array(5)].map((_, i) => (
          <div key={i} className="learn-skeleton-item"></div>
        ))}
      </div>
      <div className="learn-main-skeleton">
        <div className="learn-skeleton-title"></div>
        <div className="learn-skeleton-line"></div>
        <div className="learn-skeleton-line"></div>
        <div className="learn-skeleton-line short"></div>
      </div>
      <div className="learn-right-skeleton">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="learn-skeleton-card"></div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="learn-page">
      <LearningHero
        title="Learning Zone"
        subtitle="Discover tutorials and guided lessons to level up faster."
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        isSearching={isSearchLoading}
        onSearchSubmit={() => handleSearch(searchQuery)}
        onClearSearch={() => {
          setSearchQuery('');
          exitSearchMode();
        }}
        onOpenBrowse={() => setIsMobileMenuOpen((v) => !v)}
      />
      
      {/* Sticky category bar */}
      <div className="learn-categoryBar" role="navigation" aria-label="Tutorial categories">
        <div className="learn-categoryBar__inner">
          <button
            className={`learn-categoryBar__chip ${selectedCategoryId === 'all' ? 'is-active' : ''}`}
            onClick={() => handleCategorySelect('all')}
            type="button"
          >
            All
          </button>
          {categories.map((category) => (
            <button
              key={category.id}
              className={`learn-categoryBar__chip ${selectedCategoryId === category.id ? 'is-active' : ''}`}
              onClick={() => handleCategorySelect(category.id)}
              type="button"
            >
              {category.name}
            </button>
          ))}
        </div>
      </div>

      {isContentLoading ? (
        <ContentLoadingSkeleton />
      ) : (
        <div className="learn-container">
          <LearningSideBar
            posts={currentPosts}
            selectedPost={selectedPost}
            onPostSelect={handlePostSelect}
            isMobileOpen={isMobileMenuOpen}
            isSearching={isSearchMode}
            searchQuery={searchQuery}
            onMobileClose={() => setIsMobileMenuOpen(false)}
          />

          <main className="learn-main">
            <div className="learn-content-wrapper">
              {selectedPost ? (
                <div className="learn-content-container">
                  <PostContent
                    postId={selectedPost.id}
                    onNext={handleNextPost}
                    onPrevious={handlePreviousPost}
                    hasNext={
                      currentIndex < currentPosts.length - 1
                    }
                    hasPrevious={currentIndex > 0}
                    currentPostTitle={selectedPost.title}
                    nextPostTitle={
                      currentIndex < currentPosts.length - 1
                        ? currentPosts[currentIndex + 1]?.title
                        : null
                    }
                    previousPostTitle={
                      currentIndex > 0
                        ? currentPosts[currentIndex - 1]?.title
                        : null
                    }
                  />

                  <CommentSection postId={selectedPost.id} />
                </div>
              ) : (
                <div className="learn-no-post">
                  <div className="learn-empty-state">
                    <span className="learn-empty-icon">
                      {isSearchMode ? '🔍' : '📚'}
                    </span>

                    <h3>
                      {isSearchMode &&
                      currentPosts.length === 0
                        ? 'No results found'
                        : 'Select a tutorial to begin'}
                    </h3>

                    <p>
                      {isSearchMode &&
                      currentPosts.length === 0
                        ? `No tutorials found for "${searchQuery}". Try different keywords.`
                        : 'Choose a topic from the sidebar to start learning'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </main>

          <aside className="learn-right-sidebar">
            <div className="learn-right-content">
              <div className="learn-info-card">
                <div className="learn-card-icon">
                  🤖 Free AI Tools
                </div>

                <p>
                  Discover powerful AI tools designed to
                  help with coding, content creation,
                  productivity, and smarter learning.
                </p>

                <a
                  href="/tools/AI"
                  className="learn-card-link"
                >
                  Explore AI Tools →
                </a>
              </div>

              <div className="learn-info-card">
                <div className="learn-card-icon">
                  🎓 Free Certifications
                </div>

                <p>
                  Access free technology courses,
                  skill-based learning paths, and
                  certification programs to grow your
                  career.
                </p>

                <a
                  href="/tools/Certification"
                  className="learn-card-link"
                >
                  View Certifications →
                </a>
              </div>

              <div className="learn-info-card">
                <div className="learn-card-icon">
                  📰 Latest Blogs
                </div>

                <p>
                  Explore trending tech blogs,
                  development guides, tutorials, and the
                  latest updates from the technology world.
                </p>

                <a
                  href="/blogs"
                  className="learn-card-link"
                >
                  Read Latest Blogs →
                </a>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default Learning;
